// Helper Excel untuk Master Payroll (import / export / template).
// Memakai xlsx-js-style yang sudah menjadi dependency proyek.
import * as XLSX from "xlsx-js-style";

// Nama kolom (header) yang dipakai di file Excel. Import mem-baca berdasar header ini.
export const PAYROLL_COLS = {
  Nik: "Nik",
  NamaKaryawan: "Nama Karyawan",
  UpahPokok: "Upah Pokok",
  Keahlian: "Keahlian",
  JkIN: "Jam Kerja IN",
  JkOUT: "Jam Kerja OUT",
  THarian: "Tunjangan Harian",
  TLemburMakan: "Tunjangan Makan",
  Harikeenam: "Hari Keenam",
  IsShift: "Shift (Ya/Tidak)",
  Bpjsks: "BPJS Kesehatan (Ya/Tidak)",
  Bpjstk: "BPJS TK (Ya/Tidak)",
};

const HEADER_ORDER = [
  PAYROLL_COLS.Nik,
  PAYROLL_COLS.NamaKaryawan,
  PAYROLL_COLS.UpahPokok,
  PAYROLL_COLS.Keahlian,
  PAYROLL_COLS.JkIN,
  PAYROLL_COLS.JkOUT,
  PAYROLL_COLS.THarian,
  PAYROLL_COLS.TLemburMakan,
  PAYROLL_COLS.Harikeenam,
  PAYROLL_COLS.IsShift,
  PAYROLL_COLS.Bpjsks,
  PAYROLL_COLS.Bpjstk,
];

const COL_WIDTHS = [16, 26, 14, 12, 13, 13, 16, 15, 14, 16, 22, 18];

export const toNum = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  const n = parseFloat(String(v).replace(/[^0-9.\-]/g, ""));
  return isNaN(n) ? 0 : n;
};

export const toBool = (v) => {
  if (typeof v === "boolean") return v;
  const s = String(v).trim().toLowerCase();
  return ["1", "true", "ya", "yes", "y", "aktif", "ada"].includes(s);
};

const boolLabel = (v) => (toBool(v) ? "Ya" : "Tidak");

// Terapkan style pada baris header + lebar kolom.
function styleSheet(ws, rowCount) {
  ws["!cols"] = COL_WIDTHS.map((w) => ({ wch: w }));
  const range = XLSX.utils.decode_range(ws["!ref"]);
  for (let c = range.s.c; c <= range.e.c; c++) {
    const addr = XLSX.utils.encode_cell({ r: 0, c });
    if (!ws[addr]) continue;
    ws[addr].s = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "2F5597" } },
      alignment: { horizontal: "center", vertical: "center" },
    };
  }
  return ws;
}

// Susun 1 baris Excel dari 1 record backend GetMasterPayroll.
function rowFromRecord(r) {
  return {
    [PAYROLL_COLS.Nik]: r.Nik ?? "",
    [PAYROLL_COLS.NamaKaryawan]: r.NamaKaryawan ?? "",
    [PAYROLL_COLS.UpahPokok]: r.UpahPokok ?? 0,
    [PAYROLL_COLS.Keahlian]: r.Keahlian ?? 0,
    [PAYROLL_COLS.JkIN]: r.JkIN ?? "",
    [PAYROLL_COLS.JkOUT]: r.JkOUT ?? "",
    [PAYROLL_COLS.THarian]: r.THarian ?? 0,
    [PAYROLL_COLS.TLemburMakan]: r.TLemburMakan ?? 0,
    [PAYROLL_COLS.Harikeenam]: r.Harikeenam ?? "",
    [PAYROLL_COLS.IsShift]: boolLabel(r.IsShift),
    [PAYROLL_COLS.Bpjsks]: boolLabel(r.Bpjsks),
    [PAYROLL_COLS.Bpjstk]: boolLabel(r.Bpjstk),
  };
}

// Export daftar record ke file .xlsx (langsung memicu download).
export function exportPayrollExcel(records) {
  const rows = (records || []).map(rowFromRecord);
  const ws = XLSX.utils.json_to_sheet(rows, { header: HEADER_ORDER });
  styleSheet(ws, rows.length);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Master Payroll");
  const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  XLSX.writeFile(wb, `Master_Payroll_${ts}.xlsx`);
}

// Unduh template kosong berisi 1 baris contoh.
export function downloadPayrollTemplate() {
  const example = {
    [PAYROLL_COLS.Nik]: "SO-20240802",
    [PAYROLL_COLS.NamaKaryawan]: "(informasi saja - diabaikan saat import)",
    [PAYROLL_COLS.UpahPokok]: 3000000,
    [PAYROLL_COLS.Keahlian]: 0,
    [PAYROLL_COLS.JkIN]: "07:00",
    [PAYROLL_COLS.JkOUT]: "15:00",
    [PAYROLL_COLS.THarian]: 0,
    [PAYROLL_COLS.TLemburMakan]: 20000,
    [PAYROLL_COLS.Harikeenam]: "Saturday",
    [PAYROLL_COLS.IsShift]: "Tidak",
    [PAYROLL_COLS.Bpjsks]: "Ya",
    [PAYROLL_COLS.Bpjstk]: "Ya",
  };
  const ws = XLSX.utils.json_to_sheet([example], { header: HEADER_ORDER });
  styleSheet(ws, 1);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Template");
  XLSX.writeFile(wb, "Template_Master_Payroll.xlsx");
}

// Baca file Excel -> { rows: [payloadUpsert...], skipped: [pesan...] }
export async function parsePayrollFile(file) {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const json = XLSX.utils.sheet_to_json(ws, { defval: "" });

  const rows = [];
  const skipped = [];
  json.forEach((row, i) => {
    const excelRow = i + 2; // +1 header, +1 index 0-based
    const nik = String(row[PAYROLL_COLS.Nik] ?? row["Nik"] ?? "").trim();
    if (!nik) {
      skipped.push(`Baris ${excelRow}: Nik kosong`);
      return;
    }
    rows.push({
      Nik: nik,
      UpahPokok: toNum(row[PAYROLL_COLS.UpahPokok]),
      Keahlian: toNum(row[PAYROLL_COLS.Keahlian]),
      JkIN: String(row[PAYROLL_COLS.JkIN] ?? "").trim(),
      JkOUT: String(row[PAYROLL_COLS.JkOUT] ?? "").trim(),
      THarian: toNum(row[PAYROLL_COLS.THarian]),
      TLemburMakan: toNum(row[PAYROLL_COLS.TLemburMakan]),
      Harikeenam: String(row[PAYROLL_COLS.Harikeenam] ?? "Saturday").trim() || "Saturday",
      IsShift: toBool(row[PAYROLL_COLS.IsShift]),
      Bpjsks: toBool(row[PAYROLL_COLS.Bpjsks]),
      Bpjstk: toBool(row[PAYROLL_COLS.Bpjstk]),
    });
  });
  return { rows, skipped };
}
